import SaveIcon from '@mui/icons-material/Save';
import { CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG } from '../../constants/planogramComplianceConfig';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService, composeProductRecognitionFieldsWithShapes } from '../../services/api';
import { GtApiService, GtInferenceResponse } from '../../services/gtApi';
import { PlanogramComplianceResponse, ProductRecognitionResponse } from '../../types';
import { Audit } from '../tasks/components/Audit';

interface GroundTruthAuditProps {
  gtInferenceId: string | null;
}

export const GroundTruthAudit: React.FC<GroundTruthAuditProps> = ({ gtInferenceId }) => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [productRecognitionData, setProductRecognitionData] =
    useState<ProductRecognitionResponse | null>(null);
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingGt, setIsSavingGt] = useState(false);
  const [imageId, setImageId] = useState<string | null>(null);

  const fetchGtInference = useCallback(async () => {
    if (!gtInferenceId || !token) return;
    setIsLoading(true);
    try {
      const gtInference: GtInferenceResponse = await GtApiService.getGtInference(
        token,
        gtInferenceId
      );
      setImageId(gtInference.imageId != null ? String(gtInference.imageId) : null);
      const prData = gtInference.prInference as ProductRecognitionResponse | undefined;
      if (prData) {
        const fieldsWithShapes = composeProductRecognitionFieldsWithShapes(prData);
        setProductRecognitionData({ ...prData, ...fieldsWithShapes });
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to fetch GT inference');
    } finally {
      setIsLoading(false);
    }
  }, [gtInferenceId, token, showErrorToast]);

  useEffect(() => {
    fetchGtInference();
  }, [fetchGtInference]);

  useEffect(() => {
    const fetchPlanogramCompliance = async () => {
      if (!token || !productRecognitionData || planogramComplianceData) {
        return;
      }
      try {
        const payload = {
          ...productRecognitionData,
          config: DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG,
        };
        const data = await ApiService.createPlanogramComplianceFromPayload(token, payload, true);
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch planogram compliance');
      }
    };
    fetchPlanogramCompliance();
  }, [token, productRecognitionData, planogramComplianceData, showErrorToast]);

  const handleSaveGt = useCallback(async () => {
    if (!gtInferenceId || !token || !productRecognitionData) return;
    setIsSavingGt(true);
    try {
      const payload = { pr_inference: productRecognitionData };
      await GtApiService.updateGtInferencePr(token, gtInferenceId, payload);
      showSuccessToast('GT inference saved successfully');
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to save GT inference');
    } finally {
      setIsSavingGt(false);
    }
  }, [gtInferenceId, token, productRecognitionData, showErrorToast, showSuccessToast]);

  const saveButton = (
    <button
      onClick={handleSaveGt}
      disabled={isSavingGt || !productRecognitionData}
      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
    >
      {isSavingGt ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
      {isSavingGt ? 'Saving...' : 'Save GT'}
    </button>
  );

  return (
    <Audit
      productRecognitionData={productRecognitionData}
      planogramComplianceData={planogramComplianceData}
      isLoading={isLoading}
      imageId={imageId}
      onProductRecognitionDataChange={setProductRecognitionData}
      onPlanogramComplianceDataChange={setPlanogramComplianceData}
      actionButtons={saveButton}
    />
  );
};

export default GroundTruthAudit;
